<template>
  <v-row
    class="px-4 pb-2"
    style="margin: 0; margin-left: -12px; margin-right: -12px"
  >
    <v-col class="py-0">
      <v-autocomplete
        hide-details
        :items="orderedDuaCancellationCodes"
        item-text="text"
        item-value="code"
        label="Inställelse"
        :loading="$apollo.loading"
        v-model="dua_cancellation_code"
        :rules="[!isChecked && validatingForm ? () => true : required]"
        :disabled="item.missing || isHistoryEvent"
        :append-icon="
          isDiff('dua_cancellation_code')
            ? 'mdi-alert-octagram'
            : 'mdi-menu-down'
        "
      />
    </v-col>
    <v-col class="py-0">
      <v-autocomplete
        hide-details
        label="Hänvisning"
        v-model="dua_reference_code"
        :items="orderedDuaReferenceCodes"
        item-text="text"
        item-value="code"
        :loading="$apollo.loading"
        clearable
        :disabled="item.missing || isHistoryEvent"
        :append-icon="
          isDiff('dua_reference_code') ? 'mdi-alert-octagram' : 'mdi-menu-down'
        "
      />
    </v-col>
    <v-col class="py-0" cols="2">
      <v-text-field
        label="Beräknad"
        type="time"
        :disabled="
          cancellationCodeInstallt === dua_cancellation_code || isHistoryEvent
        "
        :rules="[
          !isChecked && validatingForm
            ? () => true
            : estimatedTimeAtLocationRule
        ]"
        :append-icon="
          isDiff('estimated_time_at_location')
            ? 'mdi-alert-octagram'
            : 'mdi-menu-down'
        "
        @blur="onEstimatedTimeBlur"
        v-model="estimated_time_at_location"
      />
    </v-col>
    <DueMessageRouteField
      :cancelSections="cancelSections"
      v-if="showRouteField"
      :item="item"
      :uuid="uuid"
      :index="index"
      :validating-form="validatingForm"
      :refForm="refForm"
      :searchTrain="searchTrain"
      :loading="$apollo.loading"
    />
    <v-col cols="1">
      <v-btn :disabled="!showDeleteButton" icon @click="$emit('remove', index)"
        ><v-icon small>mdi-delete</v-icon></v-btn
      >
    </v-col>
    <!-- <v-col class="py-0">
      <v-autocomplete
        hide-details
        v-model="dua_reason_code"
        label="Orsak"
        :items="orderedDuaReasonCodes"
        item-text="text"
        item-value="code"
        clearable
        :loading="$apollo.loading"
        :disabled="item.missing || isHistoryEvent"
        :append-icon="
          isDiff('dua_reason_code') ? 'mdi-alert-octagram' : 'mdi-menu-down'
        "
      />
    </v-col> -->
  </v-row>
</template>

<script>
import { required } from '@/common/validationRules'
import gql from 'graphql-tag'
import { get } from 'vuex-pathify'
import orderBy from 'lodash.orderby'
import {
  format,
  isAfter,
  addHours,
  isBefore,
  addDays,
  differenceInMinutes,
  addMinutes
} from 'date-fns'
import DueMessageRouteField from './DuaMessageRouteFieldV2'

const INVÄNTA_INFO_CODE = 'ANA075'
const INSTÄLLT_CANCELATION_CODE = 'ANA027'

const duaCancellationCodesOrder = [
  INSTÄLLT_CANCELATION_CODE,
  'ANA007',
  'ANA058'
]
const duaReferenceCodesOrder = ['ANA039', INVÄNTA_INFO_CODE]

const getSet = prop => ({
  get() {
    return this.form[this.uuid].data.cancel_sections[this.index][prop]
  },

  set(v) {
    let value = v
    if (!value || !value.length) {
      value = null
    }

    const form = Object.assign({}, this.form)
    const cancel_sections = form[this.uuid].data.cancel_sections
      .slice()
      .map(x => Object.assign({}, x))

    cancel_sections[this.index][prop] = value
    this.$store.dispatch('duaMessage/updateForm', {
      uuid: this.uuid,
      prop: 'cancel_sections',
      value: cancel_sections
    })
  }
})

const getSetDateTime = prop => ({
  get() {
    const dateTime = this.form[this.uuid].data.cancel_sections[this.index][prop]
    if (dateTime) return format(new Date(dateTime), 'HH:mm')
    return null
  },

  set(v) {
    let value = v
    const form = Object.assign({}, this.form)
    const cancel_sections = form[this.uuid].data.cancel_sections
      .slice()
      .map(x => Object.assign({}, x))

    if (!value || !value.length) {
      cancel_sections[this.index][prop] = null
    } else {
      const date = format(this.departureDate, 'yyyy-MM-dd')

      cancel_sections[this.index][prop] = new Date(
        format(new Date(`${date} ${value}`), 'yyyy-MM-dd HH:mm')
      )
      if (isBefore(cancel_sections[this.index][prop], this.departureDate)) {
        const addDay = addDays(new Date(), 1)
        const addDayDate = format(addDay, 'yyyy-MM-dd')
        cancel_sections[this.index][prop] = new Date(
          format(new Date(`${addDayDate} ${value}`), 'yyyy-MM-dd HH:mm')
        )
      }
    }
    this.$store.dispatch('duaMessage/updateForm', {
      uuid: this.uuid,
      prop: 'cancel_sections',
      value: cancel_sections
    })
  }
})

export default {
  name: 'DuaMessageCancelSection',

  components: {
    DueMessageRouteField
  },

  props: {
    item: Object,
    uuid: String,
    missing: Boolean,
    validatingForm: Boolean,
    index: Number,
    showDeleteButton: Boolean,
    cancelSections: Array,
    refForm: null,
    searchTrain: Object,
    departure: [String, Date]
  },

  data: () => ({
    cancellationCodeInstallt: INSTÄLLT_CANCELATION_CODE,
    invantaInfoCode: INVÄNTA_INFO_CODE,
    required
  }),

  computed: {
    model: get('duaMessage/model'),
    form: get('duaMessage/form'),
    isHistoryEvent: get('history/isHistoryEvent'),

    showRouteField() {
      return ['CANCELED_STOP', 'CANCELED', 'PARTIAL_CANCELLATION'].includes(
        this.item.text.deviation_type
      )
    },

    isChecked() {
      return this.form[this.uuid]._checked
    },

    departureDate() {
      return new Date(this.item.text.departure)
    },

    orderedDuaCancellationCodes() {
      return this.duaCancellationCodes
        ? duaCancellationCodesOrder.map(code =>
            this.duaCancellationCodes.find(x => x.code === code)
          )
        : []
    },

    orderedDuaReferenceCodes() {
      const codes = this.duaReferenceCodes
        ? duaReferenceCodesOrder.map(code =>
            this.duaReferenceCodes.find(x => x.code === code)
          )
        : []

      if (this.dua_cancellation_code !== INSTÄLLT_CANCELATION_CODE) {
        return codes.filter(x => x.code === INVÄNTA_INFO_CODE)
      }

      return codes
    },

    orderedDuaReasonCodes() {
      return orderBy(this.duaReasonCodes || [], ['text'], ['asc'])
    },

    currentTrip() {
      return this.model?.body?.find(x => x?.trip_uuid === this.uuid)
    },

    dua_cancellation_code: {
      ...getSet('dua_cancellation_code')
    },

    dua_reason_code: {
      ...getSet('dua_reason_code')
    },

    dua_reference_code: {
      ...getSet('dua_reference_code')
    },

    estimated_time_at_location: {
      ...getSetDateTime('estimated_time_at_location')
    },

    advertisedTimeFrom() {
      const fromPlace = this.cancelSections[this.index].from
      if (!fromPlace) return false

      const advertisedTime = this.searchTrain?.places?.find(
        x => x.type === 'Avgang' && x.location === fromPlace
      )

      if (!advertisedTime) return false
      return new Date(advertisedTime.advertised)
    }
  },

  watch: {
    dua_cancellation_code() {
      this.dua_reference_code = null
      this.estimated_time_at_location = null
    }
  },

  methods: {
    onEstimatedTimeBlur(value) {
      if (!value?.target?.value) return
      const form = Object.assign({}, this.form)
      const getMatrix = section =>
        (this.busDurationMap || []).find(
          m =>
            m.from_place.toLowerCase() === section.from.toLowerCase() &&
            m.to_place.toLowerCase() === section.to.toLowerCase()
        )

      const cancelSections = form[this.item.uuid]?.data.cancel_sections.reduce(
        (acc, section, i) => {
          if (i > this.index) {
            const prevSection = acc[i - 1]
            const selectedMatrix = prevSection ? getMatrix(prevSection) : false
            if (
              selectedMatrix &&
              prevSection &&
              prevSection.estimated_time_at_location
            ) {
              acc.push({
                ...section,
                estimated_time_at_location: new Date(
                  format(
                    addMinutes(
                      new Date(prevSection.estimated_time_at_location),
                      selectedMatrix.duration
                    ),
                    'yyyy-MM-dd HH:mm'
                  )
                )
              })
            } else {
              acc.push({
                ...section,
                estimated_time_at_location: null
              })
            }
          } else {
            acc.push(section)
          }

          return acc
        },
        []
      )
      this.$store.dispatch('duaMessage/updateForm', {
        uuid: this.item.uuid,
        prop: 'cancel_sections',
        value: cancelSections
      })
    },

    isDiff(prop) {
      if (!this.currentTrip) return false
      const currentProp =
        this.currentTrip.data?.cancel_sections?.[this.index]?.[prop] || null
      const formProp =
        this.form[this.uuid].data?.cancel_sections?.[this.index]?.[prop] || null

      if (currentProp && formProp && prop === 'estimated_time_at_location') {
        return (
          format(new Date(currentProp), 'yyyy-MM-dd HH:mm') !==
          format(new Date(formProp), 'yyyy-MM-dd HH:mm')
        )
      }
      return currentProp !== formProp
    },

    estimatedTimeAtLocationRule() {
      if (!this.advertisedTimeFrom) return true
      if (
        this.cancellationCodeInstallt === this.dua_cancellation_code ||
        this.missing
      ) {
        return true
      }

      if (
        !this.form[this.uuid].data.cancel_sections[this.index]
          .estimated_time_at_location
      ) {
        return true
      }

      const eta = new Date(
        this.form[this.uuid].data.cancel_sections[
          this.index
        ].estimated_time_at_location
      )

      const minDiffEtaAndDepartureDate = differenceInMinutes(
        eta,
        this.advertisedTimeFrom
      )

      if (
        isAfter(eta, new Date()) &&
        (isAfter(eta, this.advertisedTimeFrom) ||
          minDiffEtaAndDepartureDate === 0) &&
        isBefore(eta, addHours(this.advertisedTimeFrom, 16))
      ) {
        return true
      }
      return 'Måste vara efter avgångstid samt högst vara 16 timmar efter avg.'
    }
  },

  apollo: {
    busDurationMap: {
      query: gql`
        query busDurationMap {
          busDurationMap {
            from_place
            to_place
            duration
          }
        }
      `,
      fetchPolicy: 'cache-and-network'
    },
    duaCancellationCodes: {
      query: gql`
        query duaCancellationCodes {
          duaCancellationCodes {
            code
            text
          }
        }
      `
    },
    duaReferenceCodes: {
      query: gql`
        query duaReferenceCodes {
          duaReferenceCodes {
            code
            text
          }
        }
      `
    },
    duaReasonCodes: {
      query: gql`
        query duaReasonCodes {
          duaReasonCodes {
            code
            text
          }
        }
      `
    }
  }
}
</script>
